import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui';

/**
 * Stops one bad render from taking the whole console down.
 *
 * React unmounts the entire tree when a render throws, so without this a single
 * unexpected response — a field that is null where the type says it never is,
 * an endpoint returning a different shape after an upgrade — leaves an operator
 * looking at a blank white page with nothing in it to act on. That is the worst
 * possible failure mode for a tool you reach for when something is already
 * wrong.
 *
 * Two boundaries are used: one around the whole app so the shell survives, and
 * one inside the layout around the routed page, so a broken page keeps the
 * navigation usable and the operator can go somewhere else.
 */
interface Props {
  children: ReactNode;
  /** Shown above the message, to say which part failed. */
  scope?: string;
  /** Rendered instead of the default panel. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The stack is the only record of this: there is no server round-trip to
    // find it in afterwards.
    console.error('render failed', this.props.scope ?? 'app', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="w-full max-w-lg rounded-xl border border-line bg-surface p-6 shadow-card">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400">
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M18 10A8 8 0 112 10a8 8 0 0116 0zm-8-4a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 8a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-ink-900 dark:text-ink-50">
                {this.props.scope
                  ? `Something went wrong in ${this.props.scope}`
                  : 'Something went wrong'}
              </h2>
              <p className="mt-1.5 text-sm text-ink-600 dark:text-ink-300">
                The rest of the portal is unaffected. Try again, or move to
                another page and come back.
              </p>

              {/* The message, not the stack: enough to report it, without a
                  wall of minified frames. */}
              <pre className="scroll-x mt-3 max-h-32 rounded-lg border border-line bg-sunken p-3 text-xs leading-relaxed text-ink-600 dark:text-ink-300">
                {error.message || String(error)}
              </pre>

              <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={this.reset}>
                  Try again
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => window.location.reload()}
                >
                  Reload the portal
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
